import {
  useState,
  ComponentProps,
  HTMLAttributeAnchorTarget,
  MouseEventHandler,
  memo,
} from 'react';
import Image from 'next/legacy/image';
import classnames from 'classnames';
import isAbsoluteUrl from '../../../core/helpers/isAbsoluteUrl';
import Link from '../Link/Link';

const TLL_TEST_IMAGE_HOST = 'https://test472.tl-lincoln.net';
const PROXY_IMAGE_HOST_FOR_TLL_TEST = `http://${process.env.NEXT_PUBLIC_PROXY_BASE_URL_FOR_TLL_TEST_IMAGE}`;

type Props = {
  alt: NonNullable<ComponentProps<typeof Image>['alt']>;
  src: ComponentProps<typeof Image>['src'] | undefined;
  fallbackSrc: NonNullable<ComponentProps<typeof Image>['src']>;
  layout: NonNullable<ComponentProps<typeof Image>['layout']>;
  className?: ComponentProps<typeof Image>['className'];
  linkImageWrapperClassName?: ComponentProps<typeof Image>['className'];
  url?: ComponentProps<typeof Link>['href'];
  target?: HTMLAttributeAnchorTarget;
  width?: ComponentProps<typeof Image>['width'];
  height?: ComponentProps<typeof Image>['height'];
  sizes?: ComponentProps<typeof Image>['sizes'];
  objectFit?: ComponentProps<typeof Image>['objectFit'];
  children?: JSX.Element;
  title?: ComponentProps<typeof Image>['title'];
  loading?: 'lazy' | 'eager';
  priority?: ComponentProps<typeof Image>['priority'];
  quality?: ComponentProps<typeof Image>['quality'];
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  rel?: string;
};

const ImageWithFallback = ({
  alt,
  src,
  fallbackSrc,
  url,
  target,
  children,
  layout,
  className,
  linkImageWrapperClassName,
  width,
  height,
  objectFit,
  onClick,
  title,
  // Default sizes to 100vw for mobile to prevent blurry image due to upscaling,
  // and 33vw for desktop as most of our images are around 1/3 of ContentWrapper
  sizes = '(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw',
  loading = 'lazy',
  priority,
  quality = 75,
  rel = 'noopener noreferrer',
}: Props) => {
  const [imgErr, setImgErr] = useState(false);

  // NOTE: Replace the tll TEST image domain with the proxy.
  // @see https://tabist.atlassian.net/browse/DC-1029
  const source =
    typeof src === 'string' && src.match(TLL_TEST_IMAGE_HOST)
      ? src.replace(TLL_TEST_IMAGE_HOST, PROXY_IMAGE_HOST_FOR_TLL_TEST)
      : src;

  const imageSizes = ['fill', 'responsive'].includes(layout) ? sizes : undefined;

  const image = (
    <>
      <Image
        layout={layout}
        className={className}
        width={width}
        height={height}
        objectFit={objectFit}
        alt={alt}
        title={title}
        src={imgErr ? fallbackSrc : source || fallbackSrc}
        placeholder="blur"
        loading={!priority ? loading : undefined}
        sizes={imageSizes}
        priority={priority}
        quality={quality}
        blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN89+5HJAAI1QMvhp6NyAAAAABJRU5ErkJggg=="
        onError={() => {
          // NOTE: Do not report this error
          // There are multiple reasons for an image loading error.
          // In our experience, there are too many false positives
          setImgErr(true);
        }}
      />
      {children}
    </>
  );

  if (url) {
    const customLinkImageWrapperClassName = classnames(
      'relative h-full hover:opacity-90',
      linkImageWrapperClassName
    );

    return (
      <Link href={url} passHref prefetch={false}>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <a
          onClick={onClick}
          target={target || (isAbsoluteUrl(url.toString()) ? '_blank' : '_self')}
          rel={isAbsoluteUrl(url.toString()) ? rel : undefined}
        >
          <div className={customLinkImageWrapperClassName}>{image}</div>
        </a>
      </Link>
    );
  }

  return image;
};

export default memo(ImageWithFallback);
